import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  Stack,
  Divider,
  Badge,
  Button,
  Flex,
} from "@chakra-ui/react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useEffect, useState } from "react";
import BreadCrumbMaker from "../../components/BreadCrumbMaker";
import { Loading } from "../../components/Loading";
import RouteFlow from "../../components/RouteFlow";

const SERVER = import.meta.env.VITE_API_URL || "https://api.travelprym.in";

const Label = ({ children }) => (
  <Text fontSize="13px" color="#718EBF">
    {children}
  </Text>
);

const Value = ({ children }) => (
  <Text fontSize="14px" color="#1E2A5A" fontWeight="500">
    {children || "-"}
  </Text>
);

const LeadDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const isFromHistory = location.pathname.startsWith("/history");
  const isFromBookings = location.pathname.startsWith("/bookings");

  useEffect(() => {
    const fetchLead = async () => {
      try {
        const token = localStorage.getItem("prima_session");
        if (!token) throw new Error("Token missing");

        const res = await axios.get(`${SERVER}/management/lead?id=${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        // console.log("Lead fetch response:", res);
        if (res.data?.data) {
          setLead(res.data.data);
        } else if (res.data) {
          setLead(res.data);
        } else {
          setError("Invalid response format");
        }
      } catch (err) {
        setError(
          err?.response?.data?.message ||
          err.message ||
          "Failed to load lead details",
        );
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchLead();
  }, [id]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return (
      <Box p={6} bg="#F3F6FD" minH="100vh">
        <Text
          p={6}
          color="red.500"
          bg="white"
          rounded="md"
          borderLeft="4px"
          borderLeftColor="red.500"
        >
          {error}
        </Text>
      </Box>
    );
  }

  if (!lead) {
    return (
      <Box p={6} bg="#F3F6FD" minH="100vh">
        <Text p={6} bg="white" rounded="md">
          No lead found with ID: {id}
        </Text>
      </Box>
    );
  }

  return (
    <Box p={6} bg="#F3F6FD" minH="100vh">
      {/* Breadcrumb */}
      <BreadCrumbMaker
        items={
          isFromHistory
            ? [
              { title: "History", link: "/history" },
              { title: "View Lead Details", link: `/history/lead/${id}` },
            ]
            : [
              { title: "Booking", link: "/bookings" },
              { title: "View Lead Details", link: `/bookings/lead/${id}` },
            ]
        }
      />

      {/* Header with Edit Button */}
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="md" color="#1E2A5A">
          Lead Information
        </Heading>

        <Button
          size="sm"
          colorScheme="blue"
          borderRadius={5}
          px={10}
          py={5}
          onClick={() => navigate("/bookings/lead/edit/" + lead._id)}
        >
          Edit Lead
        </Button>
      </Flex>

      {/* Card */}
      <Box
        bg="white"
        borderRadius="2xl"
        p={8}
        maxW="1200px"
        minH="420px"
        boxShadow="sm"
      >
        {/* Header */}
        <Flex justify="space-between" align="center" mb={6}>
          <Heading size="sm" color="#1E2A5A">
            Lead Information
          </Heading>

          <Button
            size="sm"
            colorScheme="blue"
            borderRadius={5}
            px={10}
            py={5}
            onClick={async () => {
              await axios.post(`${SERVER}/management/retry-whatsapp`, { leadId: lead._id });
            }}
          >
            Send Details on Whatsapp
          </Button>
        </Flex>

        {/* Lead Info */}
        <SimpleGrid columns={{ base: 1, md: 4 }} spacingY={6} spacingX={10}>
          <Stack>
            <Label>Lead ID</Label>
            <Value>{lead.leadID}</Value>
          </Stack>

          <Stack>
            <Label>Customer Name</Label>
            <Value>{lead.customer?.name}</Value>
          </Stack>

          <Stack>
            <Label>Contact Number</Label>
            <Value>{lead.customer?.mobile}</Value>
          </Stack>

          <Stack>
            <Label>Date</Label>
            <Value>
              {lead.pickupDateTime
                ? new Date(lead.pickupDateTime).toLocaleDateString("en-GB")
                : "-"}
            </Value>
          </Stack>

          <Stack>
            <Label>Trip Type</Label>
            <Value>{lead.type}</Value>
          </Stack>
          {lead.type == "localRental" &&
            lead?.tripDetails?.hours &&
            lead?.tripDetails?.distance && (
              <Stack>
                <Label>Rental Package (Hour - Included Kms)</Label>
                <Value>
                  {lead?.tripDetails?.hours} Hour -{" "}
                  {lead?.tripDetails?.distance}Km
                </Value>
              </Stack>
            )}

          <Stack>
            <Label>Pickup Location</Label>
            <Value>{lead.fromLocation}</Value>
          </Stack>


          <Stack>
            <Label>Drop Location</Label>
            <Value>{lead.toLocation}</Value>
          </Stack>


          <Stack>
            <Label>Original Price</Label>
            <Value>₹{lead.fareDetails?.baseFare || lead.fareDetails?.total || 0}</Value>
          </Stack>

          <Stack>
            <Label>Discount</Label>
            <Value>₹{lead.fareDetails?.discounts || 0}</Value>
          </Stack>

          <Stack>
            <Label>Estimated Fare</Label>
            <Value>₹{lead.fareDetails?.total || lead.estimatedFare || 0}</Value>
          </Stack>

          {lead.customer?.isCorporate && lead.customer?.corporateDetails && (
            <>
              <Stack>
                <Label>Customer Type</Label>
                <Badge colorScheme="purple" w="fit-content">
                  Corporate
                </Badge>
              </Stack>

              <Stack>
                <Label>Company Name</Label>
                <Value>{lead.customer.corporateDetails.companyName}</Value>
              </Stack>

              <Stack>
                <Label>GSTIN</Label>
                <Value>{lead.customer.corporateDetails.gstNo}</Value>
              </Stack>
            </>
          )}
        </SimpleGrid>

        {(lead.tripDetails?.stops || []).length > 0 && (
          <Box mt={4}>
            <RouteFlow
              from={lead.fromLocation}
              to={lead.toLocation}
              stops={lead.tripDetails?.stops || []}
              tripType={lead.type}
            />
          </Box>
        )}

        <Divider my={6} />

        {/* Passenger Details */}
        {lead.passengers && lead.passengers.length > 0 && (
          <>
            <Heading size="sm" color="#1E2A5A" mb={4}>
              Passenger Details
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
              {lead.passengers.map((passenger, index) => (
                <Box
                  key={passenger._id || index}
                  p={4}
                  borderWidth="1px"
                  borderRadius="md"
                  borderColor="gray.200"
                  bg="gray.50"
                >
                  <Text fontWeight="600" color="#1E2A5A" mb={3}>
                    Passenger {index + 1}
                  </Text>
                  <Stack spacing={2}>
                    <Flex justify="space-between">
                      <Label>Name</Label>
                      <Value>{passenger.name || "-"}</Value>
                    </Flex>
                    <Flex justify="space-between">
                      <Label>Mobile</Label>
                      <Value>{passenger.mobile || "-"}</Value>
                    </Flex>
                    <Flex justify="space-between">
                      <Label>WhatsApp</Label>
                      <Value>{passenger.whatsapp || "-"}</Value>
                    </Flex>
                    {passenger.email && (
                      <Flex justify="space-between">
                        <Label>Email</Label>
                        <Value>{passenger.email}</Value>
                      </Flex>
                    )}
                  </Stack>
                </Box>
              ))}
            </SimpleGrid>

            <Divider my={6} />
          </>
        )}

        {/* Notes */}
        <SimpleGrid columns={3} spacing={1}>
          <Stack>
            <Label>Notes</Label>
            <Value>{lead.notes || "—"}</Value>
          </Stack>
          <Stack>
            <Label>Specific Pickup Location</Label>
            <Value>
              {lead?.specificPickupLocation || lead?.specificpickuplocation || "-"}
            </Value>
          </Stack>
          <Stack>
            <Label>Specific Drop Location</Label>
            <Value>
              {lead?.specificDropLocation || lead?.specificdroplocation || "-"}
            </Value>
          </Stack>
        </SimpleGrid>
      </Box>
    </Box>
  );
};

export default LeadDetails;
